import { type FC, useMemo } from 'react';
import NumberBall from '@/components/base/NumberBall';
import { EmptyFilterState } from './FilterBadge';
import { useStatisticsData } from '@/pages/statistics/StatisticsDataContext';

interface TripleEntry {
  triple: [number, number, number];
  count: number;
}

const TripleFrequencyChart: FC = () => {
  const { filteredDraws, loading } = useStatisticsData();
  const draws = filteredDraws;

  const tripleData = useMemo(() => {
    if (!draws.length) return [] as TripleEntry[];
    const map = new Map<string, number>();
    draws.forEach((draw) => {
      const nums = draw.numbers;
      for (let i = 0; i < nums.length; i++) {
        for (let j = i + 1; j < nums.length; j++) {
          for (let k = j + 1; k < nums.length; k++) {
            const arr = [nums[i], nums[j], nums[k]].sort((a, b) => a - b);
            const key = arr.join('-');
            map.set(key, (map.get(key) || 0) + 1);
          }
        }
      }
    });
    const entries: TripleEntry[] = [];
    map.forEach((count, key) => {
      const arr = key.split('-').map(Number);
      entries.push({ triple: arr as [number, number, number], count });
    });
    return entries.sort((a, b) => b.count - a.count).slice(0, 20);
  }, [draws]);

  const maxCount = tripleData[0]?.count || 1;

  const getBarColor = (count: number) => {
    const ratio = count / maxCount;
    if (ratio >= 0.7) return 'bg-secondary-500';
    if (ratio >= 0.5) return 'bg-secondary-400';
    if (ratio >= 0.3) return 'bg-secondary-300';
    return 'bg-secondary-200';
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-background-200 rounded w-36 mb-4"></div>
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-9 bg-background-200 rounded-lg" style={{ width: `${60 + Math.random() * 40}%` }}></div>
          ))}
        </div>
      </div>
    );
  }

  if (!draws.length) {
    return <EmptyFilterState />;
  }

  if (tripleData.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-foreground-400 text-sm">삼중 조합 데이터가 충분하지 않습니다.</p>
      </div>
    );
  }

  const totalCombos = (45 * 44 * 43) / 6; // 45C3 = 14190

  return (
    <div>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <p className="text-xs text-foreground-400">
          세 번호가 함께 당첨번호로 나온 횟수입니다. 전체 {totalCombos.toLocaleString()}개 조합 중 상위 20개를 보여줍니다.
        </p>
      </div>

      <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-2">
        {tripleData.map((entry, idx) => {
          const percentage = Math.round((entry.count / maxCount) * 100);
          return (
            <div key={`${entry.triple[0]}-${entry.triple[1]}-${entry.triple[2]}`} className="flex items-center gap-2.5 group">
              {/* Rank */}
              <span className="w-5 text-right text-xs font-bold flex-shrink-0 text-foreground-500">
                {idx + 1}
              </span>

              {/* Numbers as balls */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {entry.triple.map((n, i) => (
                  <span key={i} className="flex items-center">
                    <NumberBall number={n} size="xs" />
                    {i < 2 && <span className="text-[10px] text-foreground-300 mx-0.5">+</span>}
                  </span>
                ))}
              </div>

              {/* Bar */}
              <div className="flex-1 h-7 bg-background-50 rounded-md overflow-hidden relative min-w-[60px]">
                <div
                  className={`h-full rounded-md transition-all duration-500 ${getBarColor(entry.count)}`}
                  style={{ width: `${Math.max(percentage, 5)}%` }}
                />
                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-xs font-bold text-foreground-700">
                  {entry.count}회
                </span>
              </div>

              {/* Percentage */}
              <span className="w-10 text-right text-xs text-foreground-500 flex-shrink-0">
                {idx < 3 ? (
                  <span className="text-secondary-700 font-bold">TOP{idx + 1}</span>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      <div className="mt-4 pt-3 border-t border-background-200/60">
        <div className="p-3 rounded-lg bg-secondary-50/60 border border-secondary-200/40">
          <p className="text-xs text-secondary-700">
            <i className="ri-lightbulb-line mr-1"></i>
            상위 삼중 조합은 특정 번호대에 집중되는 경향이 있습니다.
            전체 {totalCombos.toLocaleString()}개 삼중 조합 중 대부분이 0~1회 출현에 그치며,
            2회 이상 함께 나온 조합은 매우 드물어요.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TripleFrequencyChart;